/**
 * SceneComponent - base transform component extending THREE.Object3D. Provides actor attachment,
 * lifecycle hooks, physics integration, and serialization support for all scene objects.
 */

import 'reflect-metadata';
import * as THREE from 'three';

import { type IRPCTarget, RPC_METADATA_KEY, type RPCMethodMetadata } from '../actors/ActorNetworking.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { ClassRegistry, EngineClass } from '../systems/ClassRegistry.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { Delegate } from '../utils/Delegate.js';
import { DescriptionHelper } from '../utils/DescriptionHelpers.js';
import { getPropertyMetadata, property, PropertyMetadata } from '../utils/serialization/decorator.js';
import { ISerializableObjectTag, STALE_PREFAB_CHILD_ARRAY_SLOT } from '../utils/serialization/serializable.js';
import { shortUUID } from '../utils/uuid.js';

import type { Actor } from '../actors/Actor.js';
import type { PlayerController } from '../actors/PlayerController.js';
import type { World } from '../game/World.js';
import type { IPhysicsEngine } from '../physics/PhysicsEngine.js';
import type { ComponentPhysicsOptions } from '../physics/PhysicsTypes.js';
import type { EditorPropertyChangedResult, IEditorObject } from '../utils/EditorClassMeta.js';
import type { EditorClassMeta } from '../utils/EditorClassMeta.js';
import type { SceneGraphDebugNode } from '../utils/SceneGraphUtils.js';
import type { IDumper, ILoader } from '../utils/serialization/factories.js';
import type { SerializedData } from '../utils/serialization/types.js';

/**
 * Bit flags for Object3D objects stored in userData.
 * Use these for efficient state tracking without multiple boolean properties.
 */
export enum ObjectFlags {
  None = 0,
  /**
   * Marks objects that are part of the actor's default component setup.
   * Set by {@link Actor.initialize} on all components to distinguish
   * default sub-objects from dynamically added ones.
   */
  DefaultSubObject = 1 << 0,
}

/**
 * Branded type for ObjectFlags values.
 * Ensures only valid ObjectFlags enum values can be used for flag operations.
 * Combine flags with bitwise OR: ObjectFlags.Selectable | ObjectFlags.VisibleInEditor
 */
export type ObjectFlagValue = ObjectFlags | (number & { readonly __objectFlagsBrand: true });

export type ComponentTickDelegate = Delegate<[SceneComponent, number], void>;
export type GenericComponentDelegate = Delegate<[SceneComponent], void>;

export interface ComponentDescriptionOptions {
  /** Whether to recalculate bounds for description */
  recalculateBounds?: boolean;
  /** Whether to include property details in description */
  includeDetails?: boolean;
}

/**
 * Options for configuring a SceneComponent
 */
export interface SceneComponentOptions extends Partial<Pick<THREE.Object3D,
  'name' | 'visible' | 'castShadow' | 'receiveShadow' | 'frustumCulled' |
  'renderOrder' | 'up' | 'matrixAutoUpdate' | 'matrixWorldAutoUpdate' |
  'useAbsolutePosition' | 'useAbsoluteRotation' | 'useAbsoluteScale' | 'static'
>> {
  /** Initial position in 3D space */
  position?: THREE.Vector3;
  /** Initial rotation in 3D space */
  rotation?: THREE.Euler;
  /** Initial scale in 3D space */
  scale?: THREE.Vector3;
}

type InitOptions<T> = T extends { initialize(options: infer O): any } ? O : never;

/**
 * Static symbol marker for components that have no meaningful spatial representation.
 * When present on a class, the editor suppresses transform controls (position/rotation/scale),
 * shadow properties, and the SceneComponent category in the property panel.
 *
 * @example
 * ```ts
 * static readonly [NON_SPATIAL] = true;
 * ```
 */
export const NON_SPATIAL: unique symbol = Symbol('NON_SPATIAL');

/** @returns True if the given instance's class (or any ancestor) is marked as non-spatial. */
export function isNonSpatial(target: object): boolean {
  let ctor: Function | null = target.constructor ?? null;
  while (ctor && ctor !== Function.prototype) {
    if ((ctor as any)[NON_SPATIAL]) return true;
    ctor = Object.getPrototypeOf(ctor) as Function | null;
  }
  return false;
}

@EngineClass('SceneComponent')
export class SceneComponent extends THREE.Object3D implements IEditorObject, IRPCTarget {

  static readonly EDITOR_CLASS_META: EditorClassMeta = {
  } as const;

  constructor() {
    super();
    ObjectTracker.track(this);
  }

  static readonly INLINE_PROPERTY_METADATA: Record<string, PropertyMetadata> = {
    name: { type: 'string', hidden: true },
    position: { type: 'vector3', decimals: 3, order: -10},
    rotation: { type: 'euler', decimals: 1},
    scale: { type: 'vector3', decimals: 3, showLinking: true },
    visible: { type: 'boolean' },
    castShadow: { type: 'boolean' },
    receiveShadow: { type: 'boolean' },
    static: { type: 'boolean' },
  };

  static readonly boundObject3DProperties = [
    'castShadow',
    'receiveShadow',
    'frustumCulled',
    'static'
  ];

  readonly [ISerializableObjectTag] = true;

  @property({ editorOnly: true, hidden: true })
  public editorData: Record<string, any> = {};

  /**
   * Stable identity for this component within a prefab template, minted the first time the
   * component is dumped as part of a prefab (see {@link SceneComponent.serialize}). Prefab
   * instances inherit it from the template (force-serialized via `alwaysSerializeWhenSet`),
   * so on reload the loader can match instance children to the current template by identity and
   * drop children that were deleted from the prefab. Components added locally on an instance/scene
   * (never dumped as a prefab) keep the empty default and are therefore never treated as
   * template-owned. Not a `uuid`, so it is never remapped by uuid-replacement.
   */
  @property({ hidden: true, alwaysSerializeWhenSet: true })
  public prefabNodeId: string = '';

  // this is marked as private for purpose, in general all caller should use getActor() instead
  private actor: Actor | null = null;


  /**
     * Delegate called before physics simulation each tick
     */
  public readonly onTickPrePhysics: ComponentTickDelegate = new Delegate<[SceneComponent, number], void>();

  /**
     * Delegate called after physics simulation each tick
     */
  public readonly onTickPostPhysics: ComponentTickDelegate = new Delegate<[SceneComponent, number], void>();

  /**
     * Delegate called when the component begins play
     */
  public readonly onBeginPlay: GenericComponentDelegate = new Delegate<[SceneComponent], void>();

  /**
     * Delegate called when the component ends play
     */
  public readonly onEndPlay: GenericComponentDelegate = new Delegate<[SceneComponent], void>();

  protected motionType: PhysicsMotionType | null = null;

  private lifeSpan: number = 0;
  private isTicking: boolean = false;
  private objectsToRemove: THREE.Object3D[] = [];

  protected _boundingBox: THREE.Box3 = new THREE.Box3();

  @property({ category: 'Advanced', categoryOrder: 100})
  private hiddenInGame: boolean = false;

  @property({ category: 'Advanced' })
  private editorOnly: boolean = false;

  /**
   * Whether this component should replicate when its owning actor replicates.
   * This is a simple on/off flag - the actor's replication settings control when/how replication happens.
   */
  @property()
  public replicated: boolean = false;
  protected declare readonly create: never;

  public static readonly create = function <T extends SceneComponent>(
    this: new () => T,
    ...args: T extends { initialize(): any }
      ? [options?: InitOptions<T>]
      : [options: InitOptions<T>]
  ): T {
    const component = new this();
    const [options] = args;
    component.initialize(options);
    return component;
  };

  /**
   * Called by create() to initialize the component. Override for custom init logic.
   * Note: Only called when created via create(), NOT when deserialized from save.
   * @param options - Options passed to create()
   */
  public initialize(options?: SceneComponentOptions): void {
    if (options?.position) this.position.copy(options.position);
    if (options?.rotation) this.rotation.copy(options.rotation);
    if (options?.scale) this.scale.copy(options.scale);
    if (options?.up) this.up.copy(options.up);
    this.name = options?.name ?? this.name;
    this.visible = options?.visible ?? this.visible;
    this.castShadow = options?.castShadow ?? this.castShadow;
    this.receiveShadow = options?.receiveShadow ?? this.receiveShadow;
    this.frustumCulled = options?.frustumCulled ?? this.frustumCulled;
    this.renderOrder = options?.renderOrder ?? this.renderOrder;
    this.matrixAutoUpdate = options?.matrixAutoUpdate ?? this.matrixAutoUpdate;
    this.matrixWorldAutoUpdate = options?.matrixWorldAutoUpdate ?? this.matrixWorldAutoUpdate;
    this.useAbsolutePosition = options?.useAbsolutePosition ?? this.useAbsolutePosition;
    this.useAbsoluteRotation = options?.useAbsoluteRotation ?? this.useAbsoluteRotation;
    this.useAbsoluteScale = options?.useAbsoluteScale ?? this.useAbsoluteScale;
  }

  /**
   * Called by the serializer after all properties have been deserialized.
   * Override to perform initialization that depends on deserialized properties.
   */
  public postLoad(): void {
    // Base implementation does nothing - override in subclasses
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.onBeginPlay.invoke(this);
    const world = this.getWorld();
    if (world) {
      if (this.hiddenInGame) {
        world.setSceneComponentHiddenInGame(this, true);
      }
    }
  }

  public override endPlay(): void {
    super.endPlay();
    const world = this.getWorld();
    if (world) {
      if (this.hiddenInGame) {
        world.setSceneComponentHiddenInGame(this, false);
      }
    }
    this.onEndPlay.invoke(this);
  }

  public override getActor(): Actor | null {
    return this.actor ?? this.parent?.getActor() ?? null;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    if (this.lifeSpan > 0) {
      this.lifeSpan -= deltaTime;
      if (this.lifeSpan <= 0) {
        this.removeFromParent();
        return;
      }
    }
    this.isTicking = true;
    this.onTickPrePhysics.invoke(this, deltaTime);
    this.isTicking = false;
    if (this.objectsToRemove.length) {
      this.remove(...this.objectsToRemove);
    }
    this.objectsToRemove = [];
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    this.onTickPostPhysics.invoke(this, deltaTime);
  }

  public getWorld(): World | null {
    return this.getActor()?.getWorld() ?? null;
  }

  public getPhysicsEngine(): IPhysicsEngine | null {
    return this.getActor()?.getPhysicsEngine() ?? null;
  }

  public isRootComponent(): boolean {
    return this.getActor()?.rootComponent === this;
  }

  public override remove(...objects: THREE.Object3D[]): this {
    if (this.isTicking) {
      this.objectsToRemove.push(...objects);
      return this;
    }

    super.remove(...objects);

    return this;
  }

  public override clear(): this {
    super.clear();

    return this;
  }

  public override clone(recursive?: boolean): this {
    throw new Error('clone is not supported');
  }

  public override copy(object: THREE.Object3D, recursive?: boolean): this {
    throw new Error('copy is not supported');
  }

  /**
   * Binds Object3D properties to component properties
   * @param target - The Object3D to bind properties to
   * @param propertyNames - Array of property names to bind
   */
  protected bindObject3DProperties(target: THREE.Object3D, propertyNames: string[]): void {
    for (const propertyName of propertyNames) {
      Object.defineProperty(target, propertyName, {
        get: () => (this as any)[propertyName],
        set: (value: any) => {
          (this as any)[propertyName] = value;
        },
        enumerable: true,
        configurable: true
      });
    }
  }

  public override removeFromParent(): this {
    if (this.getActor()?.isPlaying()) {
      this.endPlay();
    }

    super.removeFromParent();

    return this;
  }

  public override replacePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    super.replacePhysicsOptions(newOptions, propagateToChildren);
    return this;
  }

  public override overridePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    // NOTE: MUST NOT call replacePhysicsOptions({ ...this.options.physicsOptions, ...newOptions }) here as that will make the children inherit the parent's options.physicsOptions
    super.overridePhysicsOptions(newOptions, propagateToChildren);
    return this;
  }

  public setPhysicsEnabled(enabled: boolean, propagateToChildren: boolean = false): this {
    return this.overridePhysicsOptions({ enabled }, propagateToChildren);
  }

  public override generateDebugNode(): SceneGraphDebugNode | null {
    const children: (SceneGraphDebugNode | null)[] = [];
    this.children.forEach(child => {
      children.push(child.generateDebugNode());
    });
    return {
      title: this.getDebugName(),
      children,
      data: {
        position: this.position.toArray(),
        rotation: this.rotation.toArray(),
        scale: this.scale.toArray(),
        visible: this.visible,
        hiddenInGame: this.hiddenInGame,
        editorOnly: this.editorOnly,
        uuid: this.uuid,
      }
    };
  }

  public getLifeSpan(): number {
    return this.lifeSpan;
  }

  public setLifeSpan(lifeSpan: number): this {
    this.lifeSpan = lifeSpan;
    return this;
  }

  public override getPathName(): string {
    if (this.parent && !(this.parent instanceof THREE.Scene)) {
      return `${this.parent.getPathName()}.${this.getDebugName()}`;
    } else {
      return `${this.getActor()?.getPathName() ?? 'Null'}.${this.getDebugName()}`;
    }
  }

  public getDebugName(): string {
    return this.name ? this.name : ClassRegistry.getClassOrConstructorName(this);
  }

  public setReplicated(replicated: boolean): void {
    this.replicated = replicated;
  }

  public isReplicated(): boolean {
    return this.replicated;
  }

  public getNetConstructionData(): SerializedData | null {
    return null;
  }

  public calcBoundingBox() {

  }

  get boundingBox(): THREE.Box3 {
    return this._boundingBox;
  }

  public override describe(options?: ComponentDescriptionOptions): Record<string, any> {
    const baseDesc = {
      class: ClassRegistry.getClassNameChecked(this)
    };

    if (!options?.includeDetails) {
      return baseDesc;
    }

    if (options?.recalculateBounds) {
      this.calcBoundingBox();
    }

    const result: Record<string, any> = {
      ...baseDesc,
      position: DescriptionHelper.dumpPosition(this.getWorldPosition()),
      rotation: DescriptionHelper.dumpRotation(this.getWorldRotation()),
      scale: DescriptionHelper.dumpScale(this.getWorldScale()),
    };
    if (this.editorData && Object.keys(this.editorData).length > 0) {
      result.editorData = this.editorData;
    }
    if (!this.boundingBox.isEmpty()) {
      result.boundingBox = DescriptionHelper.dumpBoundingBox(this.boundingBox);
    }

    if (this.children.length > 0) {
      const children = this.children.map(child => {
        return child.describe(options);
      })
        .filter(child => child !== null);

      if (children.length > 0) {
        result.children = children;
      }
    }

    return result;
  }

  public isHiddenInGame(): boolean {
    return this.hiddenInGame;
  }
  public setHiddenInGame(hidden: boolean) {
    this.hiddenInGame = hidden;
    const world = this.getWorld();
    world?.setSceneComponentHiddenInGame(this, hidden);
  }

  public isEditorOnly(): boolean {
    return this.editorOnly;
  }

  public setEditorOnly(editorOnly: boolean) {
    this.editorOnly = editorOnly;
  }

  public getPlayerController(): PlayerController | null {
    return this.getActor()?.getPlayerController() ?? null;
  }

  public isPlayerControlled(): boolean {
    return this.getActor()?.isPlayerControlled() ?? false;
  }

  public serialize(dumper: IDumper): void {
    // Mint a stable prefab-node identity the first time this component is exported as part of a
    // prefab template. Instances inherit it (never mint, since they are not dumped as prefabs), so
    // the loader can later match instance children to the current template by identity.
    if (dumper.dumpingAsPrefab && !this.prefabNodeId) {
      this.prefabNodeId = shortUUID();
    }

    dumper.dumpValue('children', this.children.filter(child => !(child instanceof THREE.Group)));

    dumper.dumpProperties(this);

    // Not the best, but for now it's easiest to handle root component transform in prefab serialization here
    // this is to prevent the scene component from loading the transform from prefab to instance
    if (this.isRootComponent() && dumper.dumpingAsPrefab) {
      dumper.removeDumpedValue('position');
      dumper.removeDumpedValue('rotation');
      dumper.removeDumpedValue('scale');
    }

    if (this.isEditorOnly()) {
      dumper.markEditorOnly();
    }
  }

  public deserialize(loader: ILoader): void {
    this.name = loader.loadValue('name') ?? this.name;

    // make a shallow copy of the current children,
    // because this.children might already contain components added in constructor, or children created automatically, i.e. MeshComponent creating a THREE.Mesh
    const children = [...this.children];
    const reconcilePrefabInstanceChildren = loader.shouldReconcilePrefabInstanceChildren();

    loader.loadValue('children', children, {
      resolveArrayElement: (data, existing, index) => {
        // Stable prefab-node-identity reconciliation: when merging a prefab instance over its
        // freshly instantiated template, match serialized children to the template by `prefabNodeId`.
        // A slot whose id is gone from the template (component deleted from the prefab) is dropped;
        // local additions (no id) fall through to the legacy matching below and are kept.
        //
        // `shouldReconcilePrefabInstanceChildren()` is only true while the loaded root is a prefab
        // instance, so `existing` here is the instantiated template subtree (never freshly
        // constructed default sub-objects from a plain/template load).
        const childPrefabNodeId =
          typeof (data as { prefabNodeId?: unknown })?.prefabNodeId === 'string'
            ? (data as { prefabNodeId: string }).prefabNodeId
            : '';
        if (reconcilePrefabInstanceChildren && childPrefabNodeId) {
          const byPrefabNodeId = existing.find(
            (c): c is SceneComponent => c instanceof SceneComponent && c.prefabNodeId === childPrefabNodeId,
          );
          if (byPrefabNodeId) {
            return byPrefabNodeId;
          }
          // The instance references a prefab-template child whose id is gone from the current
          // template (deleted from the prefab) → drop it.
          return STALE_PREFAB_CHILD_ARRAY_SLOT;
        }

        // Match by name if available, fallback to index-based
        // 'data' is the dereferenced object content (already resolved if it was a $ref)
        const name = data?.name;
        if (name) {
          return existing.find(c => c.name === name) ?? existing[index];
        }
        return existing[index];
      }
    });
    // just adding the new children here, regardless of the order, maybe not an issue?
    const newChildren = children.filter(child => child && !this.children.includes(child));

    for (const child of newChildren) {
      if (child.hasFlags(ObjectFlags.DefaultSubObject)) {
        // here we assume Actor.initialize has been called which marks all the default objects with the flag
        // so any new object in the array must belong to the old setup and the decision here is to discard them
        continue;
      }
      this.add(child);
    }
    loader.loadProperties(this);

    // Migration shim: move legacy editorData.displayName → name
    if (this.editorData?.displayName) {
      this.name = this.editorData.displayName as string;
      delete this.editorData.displayName;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    // Handle button clicks for decorated methods
    if (value === true) {
      // Check if this property maps to a method on the component
      // We can check the prototype or just check if the member is a function
      if (typeof (this as any)[path] === 'function') {
        const method = (this as any)[path];
        // Ensure it's a method and not just a property holding a function (though unlikely with decorators)
        // Verify it has the button metadata?
        // Actually, if the editor triggered a change with value=true for a function, it MUST be a button.
        // The editor wouldn't call onEditorPropertyChanged for a function otherwise.
        console.log(`[SceneComponent] Invoking button method: ${path}`);
        method.call(this);
      }
    }
  }

  public onEditorSelectedChanged(isSelected: boolean, selectedActors: Actor[]): void {
    // Override in subclasses if needed
  }

  public onEditorComponentSelectionChanged(selectedComponent: SceneComponent | null, selectedActors: Actor[]): void {
    // Override in subclasses if needed
  }

  public onEditorAddToWorld(): void {
    // Override in subclasses if needed
  }

  public onEditorRemoveFromWorld(): void {
    // Override in subclasses if needed
  }

  public getConfigurationWarnings(): string[] {
    const warnings: string[] = [];
    const metadata = getPropertyMetadata(this);
    for (const [key, meta] of Object.entries(metadata)) {
      if (!meta.required) continue;
      const value = (this as any)[key];
      if (value === null || value === undefined || value === '') {
        const label = meta.description ?? key;
        warnings.push(`"${label}" is required but has not been set.`);
      }
    }
    return warnings;
  }

  public getEditorClassIcon(): string | null {
    return 'Icon_SceneComponent';
  }

  // ---------------------------------------------------------------------------
  // IRPCTarget implementation — delegates authority/ownership to owning actor
  // ---------------------------------------------------------------------------

  /** @inheritdoc */
  public hasAuthority(): boolean {
    return this.getActor()?.hasAuthority() ?? false;
  }

  /** @inheritdoc */
  public isLocalAutonomousProxy(): boolean {
    return this.getActor()?.isLocalAutonomousProxy() ?? false;
  }

  /** @inheritdoc */
  public isOwnedByLocalClient(): boolean {
    return this.getActor()?.isOwnedByLocalClient() ?? false;
  }

  /** Returns the owning actor's network object ID, used for RPC routing. */
  public getNetObjectId(): string {
    return this.getActor()!.getNetObjectId();
  }

  /**
   * Returns the component's `uuid` as its stable network sub-identifier within the actor.
   * This is used by incoming RPC messages to route to the correct component.
   * UUIDs are used instead of names because not all components have explicit names,
   * and UUIDs are always present and restored from construction data on clients.
   */
  public getNetSubId(): string {
    return this.uuid;
  }

  public get netOwningClientId(): string | null {
    return this.getActor()?.netOwningClientId ?? null;
  }

  public set netOwningClientId(_value: string | null) {
    // Ownership is set on the actor; ignored on components.
  }

  /**
   * Returns true when `methodName` is decorated as an RPC on this component's class.
   */
  public isValidRPCMethod(methodName: string): boolean {
    const metadata: RPCMethodMetadata[] = Reflect.getMetadata(RPC_METADATA_KEY, this.constructor) ?? [];
    return metadata.some(m => m.methodName === methodName);
  }

  /**
   * Sets the actor that owns this component.
   * External code should never call this directly.
   * Use actor.setRootComponent(...) instead.
   * @param actor - The actor to set as owner
   */
  public _privateSetActor(actor: Actor | null): void {
    this.actor = actor;
  }
}
